import { Col, Row, Typography } from "antd";
import EllipsisText from "components/_storybooks/EllipsisText";
import SpaceCustom from "components/_storybooks/SpaceCustom";
import CustomTable from "components/CustomTable";
import CustomTextHiddenLine from "components/CustomTextHiddenLine";
import FormInput from "components/FormInput";
import { DATE_TIME_FORMAT3, INPUT_TYPE } from "config/constants";

import { t } from "i18next";
import moment from "moment";
import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import { GET_WAREHOUSES_BY_SELLER } from "graphql/ware-house/query";

function WarehouseSelect({
  options,
  defaultValue,
  dataSource,
  onGetValue,
  warehouseSelected,
  orderData,
  promotionType,
  sellerID: sellerIDProp,
}) {
  const sellerID = sellerIDProp || orderData?.sellerID || orderData?.seller?.id;
  const { data } = useQuery(GET_WAREHOUSES_BY_SELLER, {
    variables: { sellerID },
    skip: !sellerID,
    fetchPolicy: "network-only",
  });
  const warehouses = data?.warehouse?.list?.warehouses || [];

  // Tính tổng số lượng đại lý đặt
  const totalOrderQuantity = dataSource?.reduce((sum, item) => sum + (item?.quantity || 0), 0) || 0;

  // Kiểm tra từng warehouse có đủ tồn kho không
  const checkWarehouseStock = (warehouseId) => {
    let hasEnoughStock = true;

    dataSource?.forEach((item) => {
      const stock = item?.product?.stocks?.find((s) => s?.warehouse?.id === warehouseId);
      const stockQuantity = stock?.quantity || 0;

      if (stockQuantity < item?.quantity) {
        hasEnoughStock = false;
      }
    });

    return hasEnoughStock;
  };

  let warehouseOptions = warehouses?.map((warehouse) => {
    const hasEnoughStock = checkWarehouseStock(warehouse.id);
    return {
      value: warehouse.id,
      branch: warehouse.branch,
      name: warehouse.name,
      label: (
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <span>{warehouse.name}</span>
          <span
            style={{
              color: hasEnoughStock ? "#00AB78" : "#D2434D",
              fontWeight: "500",
              fontStyle: "italic",
              fontSize: 13,
            }}
          >
            {hasEnoughStock ? "Đủ tồn" : "Thiếu"}
          </span>
        </div>
      ),
    };
  }) || [];

  if (warehouseSelected?.value && !warehouseOptions?.some((opt) => opt.value === warehouseSelected.value)) {
    warehouseOptions.push({
      value: warehouseSelected.value,
      branch: warehouseSelected.branch,
      name: warehouseSelected.label,
      label: (
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <span>{warehouseSelected.label}</span>
        </div>
      ),
    });
  }

  // Disable dates in the past
  const disabledDate = (current) => {
    return current && current < moment().startOf("day");
  };

  // Disable hours/minutes/seconds in the past for today
  const disabledTime = (current) => {
    if (!current || !moment(current).isSame(moment(), "day")) {
      return {}; // Không disable time nếu không phải hôm nay
    }

    const now = moment();
    const currentHour = now.hour();
    const currentMinute = now.minute();

    return {
      disabledHours: () => {
        // Disable tất cả giờ trước giờ hiện tại
        return Array.from({ length: currentHour }, (_, i) => i);
      },
      disabledMinutes: (selectedHour) => {
        // Nếu chọn giờ hiện tại, disable phút trước phút hiện tại
        if (selectedHour === currentHour) {
          return Array.from({ length: currentMinute }, (_, i) => i);
        }
        return [];
      },
      disabledSeconds: (selectedHour, selectedMinute) => {
        // Nếu chọn giờ và phút hiện tại, disable giây trước giây hiện tại
        if (selectedHour === currentHour && selectedMinute === currentMinute) {
          return Array.from({ length: now.second() }, (_, i) => i);
        }
        return [];
      },
    };
  };

  const renderChildrenInStock = (warehouses) => {
    return warehouses?.map((warehouse, index) => ({
      title: (
        <span
          style={
            warehouse?.id === warehouseSelected?.value
              ? { textDecoration: "underline #1953D8 2px", color: "#1953D8" }
              : {}
          }
        >
          <EllipsisText
            style={{ color: warehouse?.id === warehouseSelected?.value ? "#1953D8" : "#5b6673" }}
            title={warehouse.name}
            line={2}
          />
        </span>
      ),
      key: index,
      width: warehouse?.name?.length > 30 ? 300 : 120,
      align: "center",
      editable: "false",
      className: warehouse?.id === warehouseSelected?.value ? "highlight-col" : "",
      render: (_, record) => {
        const stockQuantity =
          record?.product?.stocks?.filter((stock) => stock?.warehouse?.id === warehouse?.id)[0]?.quantity || 0;
        return (
          <SpaceCustom>
            <Typography.Text>{stockQuantity}</Typography.Text>
            {stockQuantity < record?.quantity && warehouse?.id === warehouseSelected?.value && (
              <Typography.Text type="warning">Không đủ tồn kho</Typography.Text>
            )}
          </SpaceCustom>
        );
      },
    }));
  };

  const columns = [
    {
      title: t("order.orderDetail.parameter"),
      width: 250,
      fixed: "left",
      render: (text, record) => {
        return (
          <SpaceCustom>
            <Link target="_blank" to={`/product/detail/${record.productID}`}>
              <Typography.Link strong>{record?.productCode}</Typography.Link>
            </Link>
            <CustomTextHiddenLine text={record?.productName} strong line={2} />
            <Typography style={{ textAlign: "end", fontStyle: "italic" }}>{record?.product?.uom?.name}</Typography>
          </SpaceCustom>
        );
      },
    },
    {
      title: "Số lượng đại lý đặt",
      align: "center",
      width: 100,
      render: (text, record, index) => {
        return (
          <SpaceCustom>
            <Typography.Text>{record?.quantity || "--"}</Typography.Text>
          </SpaceCustom>
        );
      },
    },
    {
      title: "TỒN VAT",
      align: "center",
      width: 100,

      render: (text, record, index) => {
        const quantityRealStockHasVat =
          (record?.product?.stocks && record?.product?.stocks[0].quantityRealStockHasVat) ?? 0;
        return (
          <SpaceCustom>
            <Typography.Text>{quantityRealStockHasVat}</Typography.Text>
            {quantityRealStockHasVat < record?.quantity && record?.vat && (
              <Typography.Text type="warning">Không đủ tồn vat</Typography.Text>
            )}
          </SpaceCustom>
        );
      },
    },
    {
      title: "LOẠI % VAT",
      dataIndex: "vatPercent",
      align: "center",
      width: 120,
      render: (_, record) => {
        return <div>{record?.product?.vatPercent >= 0 ? `${record?.product?.vatPercent}%` : "KCT"}</div>;
      },
      editable: "true",
    },
    {
      title: "Tồn kho khả dụng",
      children: renderChildrenInStock(warehouses),
      width: 300,
    },
  ];

  const optionPaymentMethod = [
    {
      label: "Chuyển khoản/tiền mặt ngay",
      value: "CASH",
    },
    {
      label: "Công nợ",
      value: "DEBT",
    },
    {
      label: "Thanh toán khi nhận hàng (COD)",
      value: "COD",
    },
  ];

  return (
    <SpaceCustom gap={8}>
      <Row gutter={[16]} style={{ alignItems: "center" }}>
        <Col span={6}>
          <FormInput
            {...{
              type: INPUT_TYPE.SELECT,
              formItemOptions: {
                label: <b>Phương thức thanh toán</b>,
                name: ["paymentMethod"],
                rules: [{ required: true, message: t("warehouse.create.pleaseEnter") }],
              },
              inputOptions: {
                options: optionPaymentMethod,
                // style: { width: "100px" },
                // onChange: (e) => handleCurrency(e),
              },
            }}
          />
        </Col>
        {promotionType === "NO_PROMOTION" && (
          <Col span={6}>
            <FormInput
              {...{
                type: INPUT_TYPE.DATE_PICKER,
                formItemOptions: {
                  label: <b>Hạn chót giao hàng</b>,
                  name: ["deliveryDeadline"],
                  // rules: [{ required: true, message: "Vui lòng nhập hạn chót giao hàng" }],
                },
                inputOptions: {
                  format: DATE_TIME_FORMAT3,
                  showTime: true,
                  style: { width: "100%" },
                  disabledDate: disabledDate,
                  disabledTime: disabledTime,
                  placeholder: "Nhập hạn chót giao hàng",
                  // onChange: (e) => handleCurrency(e),
                },
              }}
            />
          </Col>
        )}

        <Col span={6}>
          <FormInput
            {...{
              type: INPUT_TYPE.SELECT,
              formItemOptions: {
                label: <b>{t("cart.exportWarehouseLabel")}</b>,
                name: ["warehouseID"],
                rules: [{ required: true, message: t("warehouse.create.pleaseEnter") }],
              },
              inputOptions: {
                defaultValue: defaultValue,
                options: warehouseOptions,
                style: { width: "100%" },
                onChange: (value, option) => {
                  onGetValue(option);
                },
              },
            }}
          />
        </Col>
        <Col span={6}>
          <FormInput
            {...{
              type: INPUT_TYPE.TEXT_AREA,
              formItemOptions: {
                label: <b>Ghi chú nội bộ</b>,
                name: ["internalNote"],
              },
              inputOptions: {
                style: { height: "33px" },
                placeholder: "Nhập ghi chú nội bộ",
                // onChange: (e) => handleCurrency(e),
              },
            }}
          />
        </Col>
      </Row>
      <CustomTable
        rowKey={(record) => record?.id}
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        scroll={{}}
      />
    </SpaceCustom>
  );
}

export default WarehouseSelect;
